import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { TASK_TEMPLATES } from '../../api/endpoints';
import { toast } from 'react-toastify';
import {
  buildTaskTemplateRelationParams,
  TASK_TEMPLATE_PRIORITY_OPTIONS,
} from './taskTemplateApiHelpers';

const buildFormData = (values) => {
  const fd = new FormData();
  fd.append('title', values.title ?? '');
  fd.append('priority', values.priority ?? '');
  fd.append('description', values.description ?? '');
  return fd;
};

const priorityOptionLabel = (value) => {
  const label = String(value || '');
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : '';
};

const CreateTaskTemplate = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [loadingPage, setLoadingPage] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (!isEdit || !editId) {
      setLoadingPage(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingPage(true);
      try {
        const { data } = await api.get(TASK_TEMPLATES.BY_ID(editId), {
          params: buildTaskTemplateRelationParams(),
        });
        const record = data?.data ?? data;
        if (cancelled || !record) return;
        setTitle(record.title ?? '');
        setPriority(record.priority ?? 'medium');
        setDescription(record.description ?? '');
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load task template');
        navigate('/task-templates');
      } finally {
        if (!cancelled) setLoadingPage(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, editId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.warn('Enter a title');
      return;
    }

    const values = {
      title: title.trim(),
      priority: priority || 'medium',
      description: description.trim(),
    };
    const fd = buildFormData(values);
    const q = new URLSearchParams(buildTaskTemplateRelationParams()).toString();

    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`${TASK_TEMPLATES.BY_ID(editId)}?${q}`, fd);
        toast.success('Task template updated');
      } else {
        await api.post(`${TASK_TEMPLATES.LIST}?${q}`, fd);
        toast.success('Task template created');
      }
      navigate('/task-templates');
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          (isEdit ? 'Failed to update task template' : 'Failed to create task template')
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/task-templates')} sx={{ mb: 2 }}>
          Back to task templates
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit task template' : 'Create task template'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={TASK_TEMPLATE_PRIORITY_OPTIONS}
                  value={priority}
                  onChange={(_, value) => setPriority(value ?? 'medium')}
                  getOptionLabel={priorityOptionLabel}
                  renderInput={(params) => <TextField {...params} label="Priority" fullWidth />}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  minRows={4}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Status will stay on the backend default.
                </Typography>
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? (
                    <CircularProgress size={22} />
                  ) : isEdit ? (
                    'Save changes'
                  ) : (
                    'Create task template'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate('/task-templates')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateTaskTemplate;
